import { useSearchParams } from "react-router";

import { useAccount } from "../lib/account.ts";
import { EmptyState } from "../lib/EmptyState.tsx";
import { LoadingScreen } from "../lib/Loading.tsx";
import { FOCUS_PARAMS } from "../routes/routes.constants.ts";
import { Spotlight } from "../spotlight/Spotlight.tsx";
import { AffiliationClaims } from "./AffiliationClaims.tsx";
import { AttributionServiceCases } from "./AttributionServiceCases.tsx";
import { RepresentationRequests } from "./RepresentationRequests.tsx";
import { BulkActionBar } from "./BulkActionBar.tsx";
import { VerificationCard } from "./VerificationCard.tsx";
import { useVerificationsController } from "./useVerificationsController.ts";
import type { Focus } from "./verifications.model.ts";
import {
  ASPECT_FILTERS,
  STATUS_FILTERS,
  VERIFICATION_COPY,
  VERIFICATION_TABS,
} from "./verifications.constants.ts";

import "./verifications.css";
import { Chip, ChipRow } from "../lib/Chip.tsx";

/**
 * `principalOndoId` scopes the inbox to this workspace.
 *
 * A person who also belongs to a gallery holds two, and a request addressed to
 * the gallery does not belong in their personal collector inbox — it was not
 * addressed to them.
 *
 * `me` comes off the account context rather than being drilled down, because the
 * only thing this screen wants from it is the membership list, and only to
 * decide whether there is a capacity to offer. See `capacitiesFor`.
 */
export function Verifications({ principalOndoId }: { principalOndoId: string }) {
  const { me: meState } = useAccount();
  const me = meState.status === "ready" ? meState.me : null;

  /**
   * `?request=` or `?claim=`, which is how the notifications inbox routes here.
   *
   * The tab is inferred from which one is present rather than carried as a
   * third parameter, so there is one authority for which tab to open. A URL
   * naming both is read as a request, arbitrarily but predictably — it is not a
   * URL this product builds.
   */
  const [params] = useSearchParams();
  const requestId = params.get(FOCUS_PARAMS.request);
  const claimId = params.get(FOCUS_PARAMS.claim);
  const representationId = params.get(FOCUS_PARAMS.representation);
  const focus: Focus | null = requestId
    ? { kind: "request", id: requestId }
    : claimId
      ? { kind: "claim", id: claimId }
      : representationId
        ? { kind: "representation", id: representationId }
        : null;

  const {
    view,
    setTab,
    setStatus,
    setAspect,
    toggle,
    clearSelection,
    answer,
    answerMany,
    refer,
    addDocument,
    answerClaim,
    answerRepresentation,
    openCaseLog,
    askEvidence,
    extendTarget,
    assignReviewer,
    recordFeePaid,
    correctFee,
    spotlight,
    openDocument,
    closeDocument,
    images,
    focusedCard,
  } = useVerificationsController({ me, principalOndoId, focus });

  if (view.kind === "loading") {
    return <LoadingScreen message={VERIFICATION_COPY.loading} />;
  }

  if (view.kind === "error") {
    return <p role="alert">{view.message}</p>;
  }

  const selectedTitles = view.rows
    .filter((row) => view.selected.includes(row.requestId))
    .map((row) => `${row.title} — ${row.work.title}`);

  return (
    <div className="verifications">
      <nav className="verifications__tabs" aria-label="Verifications">
        {VERIFICATION_TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-current={view.tab === entry.id}
            className={`verifications__tab ${
              view.tab === entry.id ? "verifications__tab--on" : ""
            }`}
            onClick={() => setTab(entry.id)}
          >
            {entry.label} · {view.counts[entry.id]}
          </button>
        ))}
      </nav>

      {view.tab === "requests" && (
        <>
          <div className="verifications__banner">
            <p>
              {view.counts.awaiting > 0 && (
                <strong>{view.counts.awaiting} awaiting your response. </strong>
              )}
              {VERIFICATION_COPY.awaitingBanner}
            </p>
            <p className="verifications__help">{VERIFICATION_COPY.noObligation}</p>
          </div>

          <div className="verifications__filters">
            <ChipRow label="Status">
              {STATUS_FILTERS.map((filter) => (
                <Chip
                  key={filter.id}
                  pressed={view.status === filter.id}
                  onClick={() => setStatus(filter.id)}
                >
                  {filter.label}
                </Chip>
              ))}
            </ChipRow>
            <ChipRow label="Type">
              {ASPECT_FILTERS.map((filter) => (
                <Chip
                  key={filter.id}
                  pressed={view.aspect === filter.id}
                  onClick={() => setAspect(filter.id)}
                >
                  <span aria-hidden="true">{filter.glyph}</span> {filter.label}
                </Chip>
              ))}
            </ChipRow>
          </div>
        </>
      )}

      {view.error && (
        <p role="alert" className="verifications__error">
          {view.error}
        </p>
      )}

      {/* A link that outlived what it pointed at. Said out loud, because a
          reader who clicked "Review" and landed on an ordinary list would
          reasonably conclude the link was broken rather than the request
          closed. */}
      {view.focus?.missing && (
        <p role="status" className="verifications__notice">
          {VERIFICATION_COPY.focusMissing}
        </p>
      )}

      {view.empty ? (
        <EmptyState className="verifications__empty" lead={view.empty.lead} detail={view.empty.detail} />
      ) : view.tab === "affiliations" ? (
        <AffiliationClaims claims={view.claims} busy={view.busy} onAnswer={answerClaim} />
      ) : view.tab === "representation" ? (
        <RepresentationRequests
          requests={view.representation}
          images={images}
          busy={view.busy}
          onAnswer={answerRepresentation}
        />
      ) : view.tab === "service" ? (
        <AttributionServiceCases
          cases={view.cases}
          log={view.caseLog}
          busy={view.busy}
          onOpenLog={openCaseLog}
          onAskEvidence={askEvidence}
          onExtend={extendTarget}
          onAssign={assignReviewer}
          onRecordPaid={recordFeePaid}
          onCorrect={correctFee}
        />
      ) : (
        <>
          <BulkActionBar
            selected={view.selected}
            titles={selectedTitles}
            busy={view.busy}
            onSend={answerMany}
            onClear={clearSelection}
          />
          <ul className="verifications__list">
            {view.rows.map((card) => (
              <VerificationCard
                key={card.requestId}
                id={cardId(card.requestId)}
                focused={view.focus?.id === card.requestId}
                ref={view.focus?.id === card.requestId ? focusedCard : null}
                card={card}
                capacities={view.capacities}
                imageUrl={
                  card.work.imagePath ? (images.get(card.work.imagePath) ?? null) : null
                }
                selected={view.selected.includes(card.requestId)}
                busy={view.busy}
                onToggle={() => toggle(card.requestId)}
                onAnswer={(body) => answer(card.requestId, body)}
                onRefer={(ondoId, note) => refer(card.requestId, ondoId, note)}
                onAddDocument={(files) => addDocument(card.entryId, files)}
                onOpenDocument={(documentId) => openDocument(card.requestId, documentId)}
              />
            ))}
          </ul>
        </>
      )}

      {/* One overlay for the screen rather than one per card: it renders
          nothing at all while shut, and only ever shows the set that was
          opened. */}
      <Spotlight view={spotlight} onClose={closeDocument} />
    </div>
  );
}

/**
 * The DOM id of one card.
 *
 * Prefixed rather than the bare uuid: an id that starts with a digit is not a
 * valid CSS selector, and one that collides with anything else on the page
 * would scroll to the wrong thing.
 */
function cardId(requestId: string): string {
  return `verification-${requestId}`;
}
